import { API } from "@swivl-mono/api-helper";
import { warn } from "@swivl-mono/helpers";
import {
  LocationVarInterface,
  LocationVarKey,
  LocationVarWithId,
  LocationVarsById,
  LocationVarsWithIdByKey,
  LocationVarsWithIdByKeyByGroupOrLocationId
} from "@swivl-mono/types";
import { toast } from "react-toastify";
import { create } from "zustand";

import LocationsModel from "../Locations/Locations.Model";
import { Member } from "../Member/Member.Current";
import { AlertsModel } from "../Notifications/Alerts.Model";

interface LocationVarsModelStoreInterface {
  allVariables: LocationVarsById;
  orgVariables: LocationVarsWithIdByKey;

  groupVariables: LocationVarsWithIdByKeyByGroupOrLocationId;

  locationVariables: LocationVarsWithIdByKeyByGroupOrLocationId;
  isLoaded: boolean;
  isLoading: boolean;
}

const BlankState: LocationVarsModelStoreInterface = {
  allVariables: {},
  orgVariables: {},
  groupVariables: {},
  locationVariables: {},
  isLoaded: false,
  isLoading: false
};

/**
 * Determines if a LocationVarKey represents a URL field
 * Includes fields ending with "URL" (excluding boolean "Has*" flags)
 * and known URL fields with non-standard naming
 */
export function isURLField(key: string): boolean {
  // Exclude boolean "Has*" flags that end with URL
  if (key.startsWith("Has")) {
    return false;
  }

  // Include standard URL fields (ending with "URL")
  if (key.endsWith("URL")) {
    return true;
  }

  // Include non-standard URL fields
  const specialCases = ["CTAURLNavigateTo"];
  return specialCases.includes(key);
}

/**
 * Validates whether a string is a valid URL (must start with http:// or https://)
 */
export function isValidURL(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Detects 403 permission errors from the API helper. The shared client wraps
 * server errors in a plain `Error` whose `message` is the server-provided text
 * (e.g. "Invalid Permissions") or falls back to `HTTP error 403: ...`.
 */
function isForbiddenError(error: unknown): boolean {
  if (!error) {
    return false;
  }
  const status = (error as { status?: number; statusCode?: number }).status ?? (error as { statusCode?: number }).statusCode;
  if (status === 403) {
    return true;
  }
  if (error instanceof Error) {
    return error.message === "Invalid Permissions" || error.message.startsWith("HTTP error 403");
  }
  return false;
}

const PERMISSION_ERROR_COPY = "You don't have permission to edit location details. Please contact an administrator.";

/**
 * Sanitizes URL values by removing trailing slashes
 * Returns the sanitized value and whether it was modified
 */
function sanitizeURL(value: string): { sanitized: string; wasModified: boolean } {
  if (!value) {
    return { sanitized: value, wasModified: false };
  }

  const sanitized = value.replace(/\/+$/, ""); // Remove one or more trailing slashes
  const wasModified = sanitized !== value;

  return { sanitized, wasModified };
}

export default class LocationVarsModel {
  static useState = create<LocationVarsModelStoreInterface>((set) => ({
    ...BlankState
  }));
  static setState = LocationVarsModel.useState.setState;
  static get state() {
    return LocationVarsModel.useState.getState();
  }

  static async loadAllVariables(force?: boolean) {
    if (LocationVarsModel.state.isLoaded && !force) {
      return;
    }
    if (LocationVarsModel.state.isLoading) {
      return;
    }
    if (!Member.orgId) {
      return;
    }
    LocationVarsModel.setState({
      isLoading: true
    });

    try {
      const variables = await API.LocationVars.loadVariables(Member.orgId!);

      LocationVarsModel.updateAllVariables(variables as LocationVarInterface[]);
      return true;
    } catch (error) {
      AlertsModel.errorToast("Error loading location variables");
      warn("&&& Error loading location variables", error);
      return false;
    }
  }
  static updateAllVariables(variables?: LocationVarInterface[]) {
    if (!variables) {
      return;
    }
    let state = LocationVarsModel.state;
    state.isLoading = false;
    state.isLoaded = true;
    state.allVariables = {};
    state.orgVariables = {};
    state.groupVariables = {};
    state.locationVariables = {};
    variables.forEach((variable) => {
      state.allVariables![variable.id] = variable;
      if (variable.locationId) {
        if (!state.locationVariables![variable.locationId]) {
          state.locationVariables![variable.locationId] = {};
        }

        state.locationVariables![variable.locationId]![variable.key as LocationVarKey] = {
          id: variable.id,
          value: variable.value,
          key: variable.key
        };
      } else if (variable.groupId) {
        if (!state.groupVariables![variable.groupId]) {
          state.groupVariables![variable.groupId] = {};
        }
        state.groupVariables![variable.groupId]![variable.key as LocationVarKey] = {
          id: variable.id,
          value: variable.value,
          key: variable.key
        };
      } else {
        state.orgVariables![variable.key as LocationVarKey] = {
          id: variable.id,
          value: variable.value,
          key: variable.key
        };
      }
    });
    LocationsModel.updateLocationsWithVariables(state.orgVariables, state.groupVariables, state.locationVariables);
    LocationVarsModel.setState({ ...state });
  }

  static async createVariable(key: LocationVarKey, value: string, groupId?: number, locationId?: number) {
    try {
      const newVar = await API.LocationVars.createVariable(key, value, Member.orgId!, groupId, locationId);

      const allVariables = LocationVarsModel.state.allVariables;
      LocationVarsModel.updateAllVariables([...Object.values(allVariables as LocationVarInterface[]), newVar]);
    } catch (error) {
      if (isForbiddenError(error)) {
        AlertsModel.errorToast(PERMISSION_ERROR_COPY);
      } else {
        const isDuplicate = error instanceof Error && error.message.includes("duplicate key value");
        AlertsModel.errorToast(
          isDuplicate ? "Attempt to create duplicated variable, please refresh the page to get the actual value" : "Error creating variable"
        );
      }
      warn("Error creating variable", error);
    }
  }

  static async saveVariable(variableId: number, value?: string): Promise<string | undefined> {
    try {
      let allVariables = LocationVarsModel.state.allVariables;

      if (!allVariables || !allVariables[variableId]) {
        AlertsModel.errorToast("Error editing variable - Not Found");
        return;
      }

      const variable = allVariables[variableId];

      if (value && variable.key === LocationVarKey.FMSId) {
        value = value.trim();
      }

      // Validate and sanitize URL fields
      if (value && isURLField(variable.key)) {
        if (!isValidURL(value)) {
          return;
        }

        const { sanitized, wasModified } = sanitizeURL(value);
        if (wasModified) {
          value = sanitized;
          toast.info("Removed trailing slash from URL for compatibility", {
            position: "bottom-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            icon: false,
            style: {
              background: "#FFF8F0", // Very light orange/cream
              border: "1px solid #FFD399", // Muted orange border
              color: "#333333", // Dark grey text
              fontFamily: '"Source Sans Pro", sans-serif',
              fontWeight: 400
            },
            progressStyle: {
              background: "#FF940E" // Brand orange for progress bar
            }
          });
        }
      }

      allVariables[variableId]!.value = value || "";
      LocationVarsModel.updateAllVariables(Object.values(allVariables as LocationVarInterface[]));
      if (typeof value !== "undefined" && value !== null && typeof value !== "string") {
        value = String(value);
      }
      const v = await API.LocationVars.updateVariable(Member.orgId!, variableId, value);
      // LocationVarsModel.postProcessVariableIfNeeded(v);

      // AlertsModel.successToast('Variable saved');
      return value;
    } catch (error) {
      AlertsModel.errorToast(isForbiddenError(error) ? PERMISSION_ERROR_COPY : "Error saving variable");
      warn("Error saving variable", error);
      return;
    }
  }

  static async getIsLocationSlugUnique(currentBrandSlug: string, currentLocationId: number, newLocationSlug?: string, newLocationShortLink?: string) {
    return await API.Spot.isLocationSlugUnique(Member.orgId, currentBrandSlug, currentLocationId, newLocationSlug, newLocationShortLink);
  }

  static deleteVariable(variable: LocationVarWithId) {
    let allVariables = { ...LocationVarsModel.state.allVariables };
    if (!allVariables || !allVariables[variable.id]) {
      warn("Variable not found");
      return;
    }
    delete allVariables[variable.id];
    LocationVarsModel.updateAllVariables(Object.values(allVariables as LocationVarInterface[]));
    API.LocationVars.deleteVariable(Member.orgId!, variable.id)
      .then(() => {
        // AlertsModel.successToast('Variable deleted');
      })
      .catch((error) => {
        AlertsModel.errorToast(isForbiddenError(error) ? PERMISSION_ERROR_COPY : "Error deleting variable");
        warn("Error deleting variable", error);
      });
  }
}
